// @flow
import React from 'react';
import { useSelector } from 'react-redux';
import { PrivateRoute, Redirect } from '@finect/front-core/Routes';

const PLUS_PURCHASE_PATH = '/servicios/finect-plus/comprar';

/**
 * A dónde sale quien no puede ver la página.
 *
 * El Redirect de front-core sirve para las dos vidas: en SSR marca el 302 en el staticContext
 * —antes esto era un `return null`, que devolvía la página en blanco— y en el navegador cambia la
 * URL. Va a comprar y no a login porque quien llega puede estar logado y no ser Plus, que es el caso
 * mayoritario, y porque un muro de login delante de la página de precios pierde la venta de quien
 * todavía no tiene cuenta (es lo que ya hacía el filtro de App.js).
 *
 * @param props Props de la ruta, incluido el staticContext del SSR
 */
const PurchaseRedirect = (props: Object) => <Redirect {...props} to={PLUS_PURCHASE_PATH} />;

type Props = {
  /** Escena que se pinta si el usuario es Plus */
  component: any
};

/**
 * Ruta que solo puede ver un suscriptor de Finect Plus.
 *
 * Declarar el acceso en la propia ruta es lo que evita que la lista de páginas de Plus y la de sus
 * excepciones se separen: antes el filtro vivía en App.js comparando la URL contra un array de
 * rutas públicas, y bastaba una barra final o una mayúscula para que el router pintara la escena y
 * el filtro echara de ella a quien tenía derecho a verla.
 *
 * Va encima de `PrivateRoute`, así que sigue exigiendo sesión: primero estar dentro, después ser
 * Plus. La puerta se construye en cada render y no hace falta memoizarla: PrivateRoute declara su
 * propio componente dentro de su `render`, así que la escena se remonta igual.
 *
 * Acepta las mismas props que `Route`.
 *
 * @param component Escena a pintar
 */
export const PlusRoute = ({ component: Scene, ...rest }: Props) => {
  /**
   * @param props Props de la ruta, incluido el staticContext del SSR
   */
  const PlusGate = (props: Object) => {
    const user = useSelector(state => state.user.user);

    // App no pinta las rutas hasta saber quién es, así que aquí `plus` ya está decidido.
    return user?.plus ? <Scene {...props} /> : <PurchaseRedirect {...props} />;
  };

  return <PrivateRoute {...rest} component={PlusGate} notLoggedInComponent={PurchaseRedirect} />;
};
